import _ from 'lodash';
import { inRange } from '../../../core/utils/general/general';
import { IRange } from '../../types';
import { original } from 'immer';

export function isIndexWithinRange(
  index: number,
  ranges: IRange[]
): {
  inRange: boolean;
  start: number;
  end: number;
  count: number;
} {
  for (const range of ranges) {
    // if (index >= range.start && index <= range.end) {
    if (inRange(index, range.start, range.end)) {
      return {
        inRange: true,
        start: range.start,
        end: range.end,
        count: range.end - range.start + 1,
      };
    }
  }
  return {
    inRange: false,
    start: -1,
    end: -1,
    count: 0,
  };
}

export function isRangeInsideRange(
  outerRange: IRange,
  innerRange: IRange
): boolean {
  return (
    innerRange.start >= outerRange.start && innerRange.end <= outerRange.end
  );
}

export function isRangeInsideRanges(innerRange: IRange, ranges: IRange[]) {
  for (const outerRange of ranges) {
    if (isRangeInsideRange(outerRange, innerRange)) {
      return true;
    }
  }

  return false;
}

export function isRangeWithinRange(
  outerRange: IRange,
  ranges: IRange[]
): {
  withinRange: boolean;
  start: number;
  end: number;
  count: number;
} {
  for (const innerRange of ranges) {
    if (
      innerRange.start >= outerRange.start &&
      innerRange.end <= outerRange.end
    ) {
      return {
        withinRange: true,
        start: innerRange.start,
        end: innerRange.end,
        count: innerRange.end - innerRange.start + 1,
      };
    }
  }

  return {
    withinRange: false,
    start: -1,
    end: -1,
    count: 0,
  };
}

export const doesRangeInterects = (
  range1: IRange, // In case of group it is group range
  range2: IRange
) => {
  return range1.start <= range2.end && range2.start <= range1.end;
};

export const getIntersectedRange = (
  range1: IRange,
  range2: IRange
): IRange | null => {
  const start = Math.max(range1.start, range2.start);
  const end = Math.min(range1.end, range2.end);

  // Check if there is a valid intersection
  if (start <= end) {
    return { start, end };
  }

  // No intersection
  return null;
};

export const reduceRangeByIntersection = (
  originalRange: IRange,
  intersectedRange: IRange
): IRange[] => {
  let reducedRanges: IRange[] = [];

  // Left non-overlapping range
  if (intersectedRange.start > originalRange.start) {
    reducedRanges.push({
      start: originalRange.start,
      end: intersectedRange.start - 1,
    });
  }

  // Right non-overlapping range
  if (intersectedRange.end < originalRange.end) {
    reducedRanges.push({
      start: intersectedRange.end + 1,
      end: originalRange.end,
    });
  }

  return reducedRanges;
};

export const sortRanges = (ranges: IRange[]): IRange[] =>
  [...ranges].sort((a, b) => a.start - b.start);

export const unionRanges = (range1: IRange, range2: IRange) => ({
  start: Math.min(range1.start, range2.start),
  end: Math.max(range1.end, range2.end),
});

export const isEqualRanges = (range1: IRange, range2: IRange) =>
  range1.start === range2.start && range1.end === range2.end;

export const areConsecutiveRanges = (
  range1: IRange,
  range2: IRange
): boolean => {
  return range1.end + 1 === range2.start || range2.end + 1 === range1.start;
};

export const addRangeToExistingRanges = (
  ranges: IRange[],
  newRange: IRange
): IRange[] => {
  const updatedRanges: IRange[] = [];
  let isRangeMerged = false;

  for (let existingRange of ranges) {
    if (
      doesRangeInterects(existingRange, newRange) ||
      areConsecutiveRanges(existingRange, newRange)
    ) {
      isRangeMerged = true;
      newRange = unionRanges(existingRange, newRange);
    } else {
      updatedRanges.push(existingRange);
    }
  }

  if (isRangeMerged) return addRangeToExistingRanges(updatedRanges, newRange);
  else {
    updatedRanges.push(newRange);
    return updatedRanges;
  }
};

export const removeRangeFromExistingRanges = (
  ranges: IRange[],
  newRange: IRange
) => {
  const updatedRanges: IRange[] = [];
  const revealedRanges: IRange[] = [];

  for (const range of ranges) {
    if (isEqualRanges(range, newRange) || isRangeInsideRange(newRange, range)) {
      // Do not push this to reveled ranges as it should be removed
      revealedRanges.push(range);
    } else if (
      doesRangeInterects(range, newRange)
      // isRangeInsideRange(range, newRange)
    ) {
      // If newRange is a subset/inside hidden range then you can split ranges
      const newRanges = splitRange(range, newRange);
      if (newRanges.length) updatedRanges.push(...newRanges);
      revealedRanges.push(newRange);
    } else {
      updatedRanges.push(range);
    }
  }

  return {
    updatedRanges,
    revealedRanges,
  };

  // const newRanges: IRange[] = [];

  // newRanges.forEach((range) => {
  //   if (isRangeInsideRange(newRange, range)) {
  //     const start = Math.max;
  //   } else {
  //     newRanges.push(range);
  //   }
  // });

  // ranges.filter((range) => !isRangeInsideRange(newRange, range));
};

export const splitRange = (range: IRange, splitByRange: IRange) => {
  const ranges: IRange[] = [];
  if (splitByRange.start > range.start) {
    // Add the first part of the range if splitByRange starts after range.start
    ranges.push({ start: range.start, end: splitByRange.start - 1 });
  }
  if (splitByRange.end < range.end) {
    // Add the second part of the range if splitByRange ends before range.end
    ranges.push({ start: splitByRange.end + 1, end: range.end });
  }

  return ranges;
};

// {
//   const unHiddenRanges: IRange[] = [];

//   const filteredRanges = ranges.filter((range) => {
//     if (isRangeInsideRange(newRange, range)) {
//       unHiddenRanges.push(range);
//       return false;
//     }

//     return true;
//   });

//   return {
//     filteredRanges,
//     // unHiddenRanges,
//   };
// };
